import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, TextField, Button, MenuItem,
  FormControl, FormControlLabel, RadioGroup, Radio, Checkbox, FormGroup,
  Autocomplete, Alert, CircularProgress, ToggleButtonGroup, ToggleButton,
  Snackbar, FormHelperText, Stack, Chip,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { publicScreeningAPI } from '../services/api';

type Lang = 'en' | 'fr';

const STR: Record<Lang, Record<string, string>> = {
  en: {
    title: 'Travel Health Screening',
    subtitle: 'Ebola entry form — no login required. Temp. taken at checkpoint.',
    eyebrow: 'Central African Republic · Ministry of Health',
    step1Title: 'Phone number & document scan',
    phone: 'Phone number',
    dialCode: 'Country code',
    step2Title: 'Scan your passport / ID (required)',
    scanHint: 'Take a photo or upload your passport / national ID — we will auto-fill your details below. You can still edit anything.',
    takePhoto: 'Take Photo',
    upload: 'Upload File',
    ocrRunning: 'Reading document…',
    ocrDone: 'Details filled from your document — please check and correct if needed.',
    uploadRequiredNotice: 'Enter your phone number and scan or upload your passport / ID to unlock the form below.',
    unlockHint: 'Complete phone number and document upload above to continue.',
    identity: 'Traveler Identity',
    fullName: 'Full name', sex: 'Sex', male: 'Male', female: 'Female',
    dob: 'Date of birth', age: 'Age (years)', ageHint: 'Calculated automatically from date of birth.',
    nationality: 'Nationality', idType: 'ID type', passport: 'Passport', nationalId: 'National ID', other: 'Other',
    idNumber: 'Passport / ID number', residence: 'Residence in destination country (optional)',
    travelPurpose: 'Purpose of travel',
    entry: 'Entry Information',
    pointOfEntry: 'Point of entry', entryCountry: 'Entry country', arrivalDate: 'Arrival date',
    arrivalTime: 'Arrival time (optional)', transportNumber: 'Flight / vehicle number (optional)',
    originCountry: 'Country of origin', countriesVisited: 'Countries visited in last 21 days',
    health: 'Health Information',
    symptoms: 'Do you currently have any of these symptoms?', none: 'None of the above',
    fever: 'Fever', cough: 'Cough', breathing: 'Difficulty breathing', vomiting: 'Vomiting',
    diarrhea: 'Diarrhoea', jaundice: 'Jaundice', rash: 'Skin rash', bleeding: 'Unusual bleeding',
    otherSymptom: 'Other symptom (specify)',
    contactSick: 'Have you been in contact with a sick person in the last 21 days?',
    epidemicArea: 'Have you visited an area affected by an epidemic?',
    epidemicDetails: 'Please specify the epidemic and location',
    yellowFever: 'Yellow fever vaccination (optional)', yellowFeverDate: 'Vaccination date',
    otherVaccinations: 'Other vaccinations (optional)',
    yes: 'Yes', no: 'No',
    declaration: 'I certify that the information provided is accurate and complete.',
    submit: 'Submit Screening', submitting: 'Submitting…',
    qrTitle: 'Screening Complete', qrRef: 'Reference ID',
    qrInstruction: 'Please show this QR code to the health officer at the screening station.',
    required: 'This field is required.',
    downloadPass: 'Download Pass', downloadPassWaiting: 'Pass unavailable — waiting for temperature check',
    passReadyMsg: 'Your pass is ready to download!',
    fixHighlighted: 'Please fill in the fields highlighted in red below.',
    declarationRequired: 'You must confirm this before submitting.',
    identityStep: 'Step 1 of 3', entryStep: 'Step 2 of 3', healthStep: 'Step 3 of 3',
    newScreening: 'New Screening',
    successSubtitle: 'Your screening has been recorded. Keep this QR code ready for the health officer.',
    stepPhone: 'Phone & ID', stepForm: 'Screening form', stepComplete: 'Complete',
    searchCountry: 'Search country or code…',
    secScan: 'Passport / ID Scan',
    secIdentity: 'Demographics',
    secHealth: 'Health Assessment',
    tempNote: 'Temperature will be taken by a health officer at the checkpoint.',
  },
  fr: {
    title: 'Dépistage Santé Voyageur',
    subtitle: "Formulaire d'entrée Ebola — sans connexion. Temp. au point de contrôle.",
    eyebrow: 'République Centrafricaine · Ministère de la Santé',
    step1Title: 'Numéro de téléphone & scan du document',
    phone: 'Numéro de téléphone',
    dialCode: 'Indicatif',
    step2Title: 'Scannez votre passeport / pièce d\'identité (obligatoire)',
    scanHint: 'Prenez une photo ou téléchargez votre passeport / carte d\'identité — nous remplirons vos informations ci-dessous. Vous pouvez tout modifier.',
    takePhoto: 'Prendre une photo',
    upload: 'Télécharger un fichier',
    ocrRunning: 'Lecture du document…',
    ocrDone: 'Informations remplies depuis votre document — veuillez vérifier et corriger si nécessaire.',
    uploadRequiredNotice: 'Entrez votre numéro de téléphone et scannez ou téléchargez votre passeport / pièce d\'identité pour déverrouiller le formulaire.',
    unlockHint: 'Complétez le numéro de téléphone et le scan du document ci-dessus pour continuer.',
    identity: 'Identité du voyageur',
    fullName: 'Nom complet', sex: 'Sexe', male: 'Masculin', female: 'Féminin',
    dob: 'Date de naissance', age: 'Âge (années)', ageHint: 'Calculé automatiquement à partir de la date de naissance.',
    nationality: 'Nationalité', idType: 'Type de pièce', passport: 'Passeport', nationalId: 'Carte nationale', other: 'Autre',
    idNumber: 'Numéro de passeport / pièce', residence: 'Résidence dans le pays de destination (facultatif)',
    travelPurpose: 'Motif du voyage',
    entry: 'Informations d\'entrée',
    pointOfEntry: 'Point d\'entrée', entryCountry: 'Pays d\'entrée', arrivalDate: 'Date d\'arrivée',
    arrivalTime: 'Heure d\'arrivée (facultatif)', transportNumber: 'Numéro de vol / véhicule (facultatif)',
    originCountry: 'Pays d\'origine', countriesVisited: 'Pays visités au cours des 21 derniers jours',
    health: 'Informations de santé',
    symptoms: 'Avez-vous actuellement l\'un de ces symptômes ?', none: 'Aucun des symptômes ci-dessus',
    fever: 'Fièvre', cough: 'Toux', breathing: 'Difficulté à respirer', vomiting: 'Vomissements',
    diarrhea: 'Diarrhée', jaundice: 'Jaunisse', rash: 'Éruption cutanée', bleeding: 'Saignement inhabituel',
    otherSymptom: 'Autre symptôme (précisez)',
    contactSick: 'Avez-vous été en contact avec une personne malade au cours des 21 derniers jours ?',
    epidemicArea: 'Avez-vous visité une zone touchée par une épidémie ?',
    epidemicDetails: 'Veuillez préciser l\'épidémie et le lieu',
    yellowFever: 'Vaccination contre la fièvre jaune (facultatif)', yellowFeverDate: 'Date de vaccination',
    otherVaccinations: 'Autres vaccinations (facultatif)',
    yes: 'Oui', no: 'Non',
    declaration: 'Je certifie que les informations fournies sont exactes et complètes.',
    submit: 'Soumettre le dépistage', submitting: 'Envoi en cours…',
    qrTitle: 'Dépistage terminé', qrRef: 'Numéro de référence',
    qrInstruction: 'Veuillez présenter ce code QR à l\'agent de santé au poste de dépistage.',
    required: 'Ce champ est obligatoire.',
    downloadPass: 'Télécharger le pass', downloadPassWaiting: 'Pass indisponible — en attente du contrôle de température',
    passReadyMsg: 'Votre pass est prêt à être téléchargé !',
    fixHighlighted: 'Veuillez remplir les champs surlignés en rouge ci-dessous.',
    declarationRequired: 'Vous devez confirmer ceci avant de soumettre.',
    identityStep: 'Étape 1 sur 3', entryStep: 'Étape 2 sur 3', healthStep: 'Étape 3 sur 3',
    newScreening: 'Nouveau dépistage',
    successSubtitle: 'Votre dépistage a été enregistré. Gardez ce code QR prêt pour l\'agent de santé.',
    stepPhone: 'Téléphone & pièce', stepForm: 'Formulaire', stepComplete: 'Terminé',
    searchCountry: 'Rechercher un pays ou un indicatif…',
    secScan: 'Scan Passeport / Pièce d\'Identité',
    secIdentity: 'Données Démographiques',
    secHealth: 'Bilan de Santé',
    tempNote: 'La température sera prise par un agent de santé au point de contrôle.',
  },
};

const SYMPTOM_KEYS = ['fever', 'cough', 'breathing', 'vomiting', 'diarrhea', 'jaundice', 'rash', 'bleeding'] as const;

const CAR = {
  blue: '#003082',
  tealDeep: '#0A5C52',
  tealMid: '#1A7A6E',
  tealLight: '#E8F5F3',
  amber: '#E6A800',
  amberBg: '#FFF8E1',
  bg: '#F0F4F8',
  border: '#E2E8F0',
  textDark: '#1A1A2E',
  textMuted: '#6B7280',
  red: '#BC0026',
  green: '#289728',
};

const TOUCH_SX = {
  '& .MuiRadio-root, & .MuiCheckbox-root': { p: 1.1 },
  '& .MuiFormControlLabel-label': { fontSize: '0.95rem' },
};

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fff' } };

type DialCodeOption = { name: string; iso2: string; dial_code: string };

const FlagBar = () => (
  <Box sx={{
    height: 5,
    background: `linear-gradient(to right,
      ${CAR.blue} 20%,
      #fff 20% 40%,
      ${CAR.green} 40% 60%,
      #FFCB00 60% 80%,
      ${CAR.red} 80%)`,
  }} />
);

const FormCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{
    bgcolor: '#fff', borderRadius: '14px', border: `1px solid ${CAR.border}`,
    overflow: 'hidden', mb: 2,
  }}>
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.25,
      px: 2.5, py: 2, borderBottom: `1px solid ${CAR.border}`, bgcolor: CAR.tealLight,
    }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: '50%', bgcolor: CAR.tealDeep,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff' }} />
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: CAR.tealDeep }}>{title}</Typography>
    </Box>
    <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>{children}</Box>
  </Box>
);

const StepperBar: React.FC<{ step: 1 | 2 | 3; lang: Lang }> = ({ step, lang }) => {
  const labels = lang === 'fr'
    ? ['Téléphone & pièce', 'Formulaire', 'Terminé']
    : ['Phone & ID', 'Screening form', 'Complete'];
  return (
    <Box sx={{ display: 'flex', mb: 3.5, gap: 0 }}>
      {labels.map((label, i) => {
        const n = i + 1;
        const done = step > n;
        const active = step === n;
        return (
          <Box key={label} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
            {i < labels.length - 1 && (
              <Box sx={{
                position: 'absolute', top: 14, left: '60%', width: '80%', height: 2,
                bgcolor: done || active ? CAR.amber : CAR.border,
                zIndex: 0,
              }} />
            )}
            <Box sx={{
              width: 28, height: 28, borderRadius: '50%', zIndex: 1, mb: 0.75,
              border: `2px solid ${done ? CAR.tealDeep : active ? CAR.amber : CAR.border}`,
              bgcolor: done ? CAR.tealDeep : active ? CAR.amber : '#fff',
              color: done || active ? '#fff' : CAR.textMuted,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 11, fontWeight: 600,
              boxShadow: active ? '0 0 0 4px rgba(230,168,0,0.18)' : 'none',
            }}>
              {done ? '✓' : n}
            </Box>
            <Typography sx={{
              fontSize: 10, fontWeight: 500, textAlign: 'center', textTransform: 'uppercase',
              letterSpacing: '0.04em', color: active ? CAR.amber : done ? CAR.tealDeep : CAR.textMuted,
            }}>
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const ScreeningHeader: React.FC<{
  lang: Lang;
  onLang: (l: Lang) => void;
}> = ({ lang, onLang }) => (
  <Box sx={{ bgcolor: '#fff', borderBottom: `1px solid ${CAR.border}` }}>
    <Box sx={{
      maxWidth: 580, mx: 'auto', px: 2.5, py: 2,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
    }}>
      <Box component="img" src="/logo/voyage_sante_logo.jpg" alt="Voyage Santé+"
        sx={{ height: 44, width: 'auto', maxWidth: 140, objectFit: 'contain' }} />
      <ToggleButtonGroup size="small" value={lang} exclusive
        onChange={(_, v) => v && onLang(v)}
        sx={{
          border: `1px solid ${CAR.border}`, borderRadius: '6px', bgcolor: '#f8f9fa',
          '& .MuiToggleButton-root': { border: 'none', py: 0.5, px: 1.5, fontSize: 12, fontWeight: 600, color: CAR.textMuted },
          '& .Mui-selected': { bgcolor: CAR.tealDeep, color: '#fff !important' },
        }}>
        <ToggleButton value="fr">FR</ToggleButton>
        <ToggleButton value="en">EN</ToggleButton>
      </ToggleButtonGroup>
      <Box component="img" src="/logo/car_coat_of_arms.svg" alt="République Centrafricaine"
        sx={{ height: 48, width: 'auto', maxWidth: 56, objectFit: 'contain' }} />
    </Box>
  </Box>
);

const RequiredMark = () => <Box component="span" sx={{ color: CAR.red }}> *</Box>;

const PublicScreeningPage: React.FC = () => {
  const [lang, setLang] = useState<Lang>('en');
  const t = (k: string) => STR[lang][k] || k;

  const [phase, setPhase] = useState<'form' | 'complete'>('form');
  const [error, setError] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const fieldRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setFieldRef = (key: string) => (el: HTMLDivElement | null) => { fieldRefs.current[key] = el; };

  const [dialCode, setDialCode] = useState('+236');
  const [dialCodes, setDialCodes] = useState<DialCodeOption[]>([]);
  const [phoneLocal, setPhoneLocal] = useState('');
  const [contactId, setContactId] = useState<number | null>(null);
  const [ocrRunning, setOcrRunning] = useState(false);
  const [ocrDone, setOcrDone] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);

  const [entryCities, setEntryCities] = useState<{ id: string; title: string }[]>([]);
  const [countries, setCountries] = useState<{ value: string; label: string }[]>([]);

  const [fullName, setFullName] = useState('');
  const [sex, setSex] = useState('');
  const [dob, setDob] = useState('');
  const [age, setAge] = useState('');
  const [nationality, setNationality] = useState('');
  const [idType, setIdType] = useState('passport');
  const [idNumber, setIdNumber] = useState('');
  const [residence, setResidence] = useState('');
  const [travelPurpose, setTravelPurpose] = useState('');

  const [entryType, setEntryType] = useState('Air-Port');
  const [entryCity, setEntryCity] = useState('');
  const [entryCountry] = useState('Central African Republic');
  const [arrivalDate, setArrivalDate] = useState(new Date().toISOString().slice(0, 10));
  const [arrivalTime, setArrivalTime] = useState('');
  const [transportNumber, setTransportNumber] = useState('');
  const [originCountry, setOriginCountry] = useState('');
  const [countriesVisited, setCountriesVisited] = useState<string[]>([]);

  const [symptoms, setSymptoms] = useState<Record<string, boolean>>({});
  const [symptomOther, setSymptomOther] = useState('');
  const [contactSick, setContactSick] = useState<'yes' | 'no' | ''>('');
  const [epidemicArea, setEpidemicArea] = useState<'yes' | 'no' | ''>('');
  const [epidemicDetails, setEpidemicDetails] = useState('');
  const [yellowFever, setYellowFever] = useState<'yes' | 'no' | ''>('');
  const [yellowFeverDate, setYellowFeverDate] = useState('');
  const [otherVaccinations, setOtherVaccinations] = useState('');

  const [declaration, setDeclaration] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [passReady, setPassReady] = useState(false);
  const [showReadySnackbar, setShowReadySnackbar] = useState(false);

  const formUnlocked = phoneLocal.trim().length >= 6 && fileUploaded;
  const progressStep: 1 | 2 | 3 = phase === 'complete' ? 3 : formUnlocked ? 2 : 1;

  useEffect(() => {
    publicScreeningAPI.dialCodes().then((d) => {
      const sorted = [...(d.dial_codes || [])].sort((a: DialCodeOption, b: DialCodeOption) =>
        a.name.localeCompare(b.name, lang === 'fr' ? 'fr' : 'en')
      );
      setDialCodes(sorted);
    }).catch(() => {});
  }, [lang]);

  const selectedDialCode = dialCodes.find((d) => d.dial_code === dialCode) || null;

  const resetScreening = () => {
    setPhase('form');
    setError(null);
    setSubmitAttempted(false);
    setDialCode('+236');
    setPhoneLocal('');
    setContactId(null);
    setOcrRunning(false);
    setOcrDone(false);
    setFileUploaded(false);
    setFullName('');
    setSex('');
    setDob('');
    setAge('');
    setNationality('');
    setIdType('passport');
    setIdNumber('');
    setResidence('');
    setTravelPurpose('');
    setEntryType('Air-Port');
    setEntryCity('');
    setArrivalDate(new Date().toISOString().slice(0, 10));
    setArrivalTime('');
    setTransportNumber('');
    setOriginCountry('');
    setCountriesVisited([]);
    setSymptoms({});
    setSymptomOther('');
    setContactSick('');
    setEpidemicArea('');
    setEpidemicDetails('');
    setYellowFever('');
    setYellowFeverDate('');
    setOtherVaccinations('');
    setDeclaration(false);
    setSubmitting(false);
    setResult(null);
    setPassReady(false);
    setShowReadySnackbar(false);
    fieldRefs.current = {};
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (phase !== 'complete' || !result?.screening_id || passReady) return;
    const check = () => {
      publicScreeningAPI.status(result.screening_id).then((d) => {
        if (d.ready) {
          setPassReady(true);
          setShowReadySnackbar(true);
        }
      }).catch(() => {});
    };
    check();
    const interval = setInterval(check, 15000);
    return () => clearInterval(interval);
  }, [phase, result, passReady]);

  useEffect(() => {
    if (!formUnlocked) return;
    publicScreeningAPI.entryOptions(entryType).then((d) => setEntryCities(d.entry_cities || [])).catch(() => {});
  }, [formUnlocked, entryType]);

  useEffect(() => {
    if (!formUnlocked) return;
    publicScreeningAPI.countries(lang).then((d) => setCountries(d.countries || [])).catch(() => {});
  }, [formUnlocked, lang]);

  const handleOcrFile = async (file: File) => {
    setError(null);
    const phone = `${dialCode}${phoneLocal}`.replace(/\s+/g, '');
    if (!phoneLocal.trim()) { setError(t('required')); return; }
    setOcrRunning(true);
    try {
      let cid = contactId;
      if (!cid) {
        const startRes = await publicScreeningAPI.start(phone);
        cid = startRes.contact_id;
        setContactId(cid);
      }
      try {
        const res = await publicScreeningAPI.uploadPassport(file, cid!);
        if (res.full_name && res.full_name !== '—') setFullName(res.full_name);
        if (res.sex) setSex(res.sex === 'M' ? 'Male' : res.sex === 'F' ? 'Female' : res.sex);
        if (res.date_of_birth && res.date_of_birth !== '—') setDob(res.date_of_birth);
        if (res.nationality && res.nationality !== '—') setNationality(res.nationality);
        if (res.doc_number && res.doc_number !== '—') setIdNumber(res.doc_number);
        setOcrDone(true);
      } catch {
        setError('Could not read the document — you can still fill the form manually.');
      }
      setFileUploaded(true);
    } catch {
      setError('Could not start screening. Please try again.');
    } finally {
      setOcrRunning(false);
    }
  };

  useEffect(() => {
    if (!dob) return;
    const birth = new Date(dob);
    if (isNaN(birth.getTime())) return;
    const today = new Date();
    let years = today.getFullYear() - birth.getFullYear();
    const hasHadBirthdayThisYear =
      today.getMonth() > birth.getMonth() ||
      (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
    if (!hasHadBirthdayThisYear) years -= 1;
    if (years >= 0 && years <= 120) setAge(String(years));
  }, [dob]);

  const toggleSymptom = (key: string) => {
    setSymptoms((prev): Record<string, boolean> => {
      if (key === 'none') return prev.none ? {} : { none: true };
      return { ...prev, [key]: !prev[key], none: false };
    });
  };

  const fieldErrors = {
    fullName: !fullName.trim(),
    sex: !sex,
    dob: !dob,
    age: !age,
    nationality: !nationality.trim(),
    idNumber: !idNumber.trim(),
    travelPurpose: !travelPurpose,
    entryCity: !entryCity,
    originCountry: !originCountry.trim(),
    symptoms: !SYMPTOM_KEYS.some((k) => symptoms[k]) && !symptoms.none,
    contactSick: !contactSick,
    epidemicArea: !epidemicArea,
    declaration: !declaration,
  };
  const FIELD_ORDER = [
    'fullName', 'sex', 'dob', 'age', 'nationality', 'idNumber', 'travelPurpose',
    'entryCity', 'originCountry', 'symptoms', 'contactSick', 'epidemicArea', 'declaration',
  ] as const;
  const showErr = (key: keyof typeof fieldErrors) => submitAttempted && fieldErrors[key];

  const handleSubmit = async () => {
    setError(null);
    setSubmitAttempted(true);
    const firstBad = FIELD_ORDER.find((k) => fieldErrors[k]);
    if (firstBad) {
      setError(t('fixHighlighted'));
      fieldRefs.current[firstBad]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        contact_id: contactId,
        phone_number: `${dialCode}${phoneLocal}`.replace(/\s+/g, ''),
        full_name: fullName || null,
        sex: sex || null,
        date_of_birth: dob || null,
        age: age ? Number(age) : null,
        nationality: nationality || null,
        id_type: idType || null,
        id_number: idNumber || null,
        residence_host_country: residence || null,
        travel_purpose: travelPurpose || null,
        entry_type: entryType || null,
        entry_city: entryCity || null,
        entry_country: entryCountry || null,
        arrival_date: arrivalDate || null,
        arrival_time: arrivalTime || null,
        transport_number: transportNumber || null,
        country_of_origin: originCountry || null,
        countries_visited_21d: countriesVisited,
        symptom_fever: !!symptoms.fever,
        symptom_cough: !!symptoms.cough,
        symptom_breathing: !!symptoms.breathing,
        symptom_vomiting: !!symptoms.vomiting,
        symptom_diarrhea: !!symptoms.diarrhea,
        symptom_jaundice: !!symptoms.jaundice,
        symptom_rash: !!symptoms.rash,
        symptom_bleeding: !!symptoms.bleeding,
        symptom_other: symptomOther || null,
        contact_with_sick: contactSick === 'yes',
        visited_epidemic_area: epidemicArea === 'yes',
        epidemic_details: epidemicArea === 'yes' ? epidemicDetails : null,
        yellow_fever_vaccinated: yellowFever === 'yes',
        yellow_fever_date: yellowFever === 'yes' ? yellowFeverDate || null : null,
        other_vaccinations: otherVaccinations || null,
      };
      const res = await publicScreeningAPI.submit(payload);
      setResult(res);
      setPhase('complete');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setError('Submission failed — please check the form and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: CAR.bg, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <FlagBar />
      <ScreeningHeader lang={lang} onLang={setLang} />

      <Box sx={{ maxWidth: 580, mx: 'auto', px: 2, pt: 3, pb: 5 }}>
        {phase === 'form' && (
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: CAR.tealMid, mb: 0.75 }}>
              {t('eyebrow')}
            </Typography>
            <Typography sx={{ fontSize: 22, fontWeight: 600, color: CAR.textDark, mb: 0.5 }}>{t('title')}</Typography>
            <Typography sx={{ fontSize: 13, color: CAR.textMuted }}>{t('subtitle')}</Typography>
          </Box>
        )}

        <StepperBar step={progressStep} lang={lang} />

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        {phase === 'form' && (
          <>
            <FormCard title={t('secScan')}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: CAR.textDark }}>{t('step1Title')}</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Autocomplete
                  sx={{ width: { xs: '100%', sm: 280 }, flexShrink: 0 }}
                  options={dialCodes}
                  value={selectedDialCode}
                  onChange={(_, v) => setDialCode(v?.dial_code || '+236')}
                  getOptionLabel={(o) => `${o.dial_code} — ${o.name}`}
                  isOptionEqualToValue={(a, b) => a.dial_code === b.dial_code && a.iso2 === b.iso2}
                  filterOptions={(opts, { inputValue }) => {
                    const q = inputValue.trim().toLowerCase();
                    if (!q) return opts;
                    return opts.filter((o) =>
                      o.name.toLowerCase().includes(q) ||
                      o.iso2.toLowerCase().includes(q) ||
                      o.dial_code.replace('+', '').includes(q.replace('+', ''))
                    );
                  }}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={`${option.iso2}-${option.dial_code}`}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                        <Chip label={option.dial_code} size="small" sx={{ fontWeight: 700, minWidth: 56 }} />
                        <Typography variant="body2" sx={{ flex: 1 }}>{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{option.iso2}</Typography>
                      </Stack>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField {...params} label={t('dialCode')} placeholder={t('searchCountry')} sx={fieldSx} />
                  )}
                />
                <TextField
                  fullWidth label={t('phone')} value={phoneLocal}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  onChange={(e) => setPhoneLocal(e.target.value.replace(/[^\d]/g, ''))}
                  sx={fieldSx}
                />
              </Box>

              <Typography variant="body2" sx={{ color: CAR.textMuted, mt: 1 }}>{t('scanHint')}</Typography>
              <Box sx={{
                border: `2px dashed ${CAR.border}`, borderRadius: 2.5, p: 2.5, textAlign: 'center',
                bgcolor: '#FAFBFC', transition: 'border-color 0.15s',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                  <Button component="label" variant="outlined" size="medium"
                    sx={{ borderRadius: 2, borderColor: CAR.tealDeep, color: CAR.tealDeep, fontWeight: 600 }}>
                    {t('takePhoto')}
                    <input hidden type="file" accept="image/*" capture="environment"
                      onChange={(e) => e.target.files?.[0] && handleOcrFile(e.target.files[0])} />
                  </Button>
                  <Button component="label" variant="outlined" size="medium"
                    sx={{ borderRadius: 2, borderColor: CAR.tealDeep, color: CAR.tealDeep, fontWeight: 600 }}>
                    {t('upload')}
                    <input hidden type="file" accept="image/*,.pdf"
                      onChange={(e) => e.target.files?.[0] && handleOcrFile(e.target.files[0])} />
                  </Button>
                  {ocrRunning && <CircularProgress size={24} sx={{ alignSelf: 'center', color: CAR.tealDeep }} />}
                </Box>
              </Box>
              {ocrRunning && <Alert severity="info" sx={{ borderRadius: 2 }}>{t('ocrRunning')}</Alert>}
              {ocrDone && !ocrRunning && <Alert severity="success" sx={{ borderRadius: 2 }}>{t('ocrDone')}</Alert>}
              {!formUnlocked && (
                <Alert severity="warning" sx={{ borderRadius: 2, bgcolor: CAR.amberBg, border: '1px solid #FFE082' }}>
                  {phoneLocal.trim() ? t('uploadRequiredNotice') : t('unlockHint')}
                </Alert>
              )}
            </FormCard>

            {formUnlocked && (
              <>
                <FormCard title={t('secIdentity')}>
                  <Box ref={setFieldRef('fullName')}>
                    <TextField fullWidth label={<>{t('fullName')}<RequiredMark /></>} value={fullName}
                      onChange={(e) => setFullName(e.target.value)} inputProps={{ maxLength: 100 }}
                      error={showErr('fullName')} helperText={showErr('fullName') ? t('required') : ' '}
                      sx={fieldSx} />
                  </Box>
                  <Box ref={setFieldRef('sex')}>
                    <FormControl error={showErr('sex')}>
                      <Typography variant="body2" sx={{ mb: 0.5, color: showErr('sex') ? CAR.red : CAR.textDark }}>
                        {t('sex')}<RequiredMark />
                      </Typography>
                      <RadioGroup row value={sex} onChange={(e) => setSex(e.target.value)} sx={TOUCH_SX}>
                        <FormControlLabel value="Male" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label={t('male')} />
                        <FormControlLabel value="Female" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label={t('female')} />
                      </RadioGroup>
                      {showErr('sex') && <FormHelperText>{t('required')}</FormHelperText>}
                    </FormControl>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box ref={setFieldRef('dob')} sx={{ flex: 1, minWidth: 150 }}>
                      <TextField fullWidth label={<>{t('dob')}<RequiredMark /></>} type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                        InputLabelProps={{ shrink: true }} inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                        error={showErr('dob')} helperText={showErr('dob') ? t('required') : ' '}
                        sx={fieldSx} />
                    </Box>
                    <Box ref={setFieldRef('age')} sx={{ flex: 1, minWidth: 130 }}>
                      <TextField fullWidth label={<>{t('age')}<RequiredMark /></>} type="number" value={age} onChange={(e) => setAge(e.target.value)}
                        inputProps={{ min: 0, max: 120 }} error={showErr('age')}
                        helperText={showErr('age') ? t('required') : t('ageHint')}
                        sx={fieldSx} />
                    </Box>
                  </Box>
                  <Box ref={setFieldRef('nationality')}>
                    <Autocomplete
                      options={countries.map((c) => c.label)}
                      value={nationality}
                      onChange={(_, v) => setNationality(v || '')}
                      renderInput={(params) => (
                        <TextField {...params} label={<>{t('nationality')}<RequiredMark /></>}
                          error={showErr('nationality')} helperText={showErr('nationality') ? t('required') : ' '}
                          sx={fieldSx} />
                      )}
                      freeSolo
                    />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>{t('idType')}</Typography>
                    <RadioGroup row value={idType} onChange={(e) => setIdType(e.target.value)} sx={TOUCH_SX}>
                      <FormControlLabel value="passport" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label={t('passport')} />
                      <FormControlLabel value="national_id" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label={t('nationalId')} />
                      <FormControlLabel value="other" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label={t('other')} />
                    </RadioGroup>
                  </Box>
                  <Box ref={setFieldRef('idNumber')}>
                    <TextField fullWidth label={<>{t('idNumber')}<RequiredMark /></>} value={idNumber} onChange={(e) => setIdNumber(e.target.value)}
                      inputProps={{ maxLength: 30 }} error={showErr('idNumber')} helperText={showErr('idNumber') ? t('required') : ' '}
                      sx={fieldSx} />
                  </Box>
                  <TextField fullWidth label={t('residence')} value={residence} onChange={(e) => setResidence(e.target.value)} sx={fieldSx} />
                  <Box ref={setFieldRef('travelPurpose')}>
                    <TextField fullWidth label={<>{t('travelPurpose')}<RequiredMark /></>} value={travelPurpose} onChange={(e) => setTravelPurpose(e.target.value)} select
                      error={showErr('travelPurpose')} helperText={showErr('travelPurpose') ? t('required') : ' '}
                      sx={fieldSx}>
                      {['Tourism', 'Business', 'Transit', 'Health', 'Official', 'Other'].map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                    </TextField>
                  </Box>
                </FormCard>

                <FormCard title={t('entry')}>
                  <RadioGroup row value={entryType} onChange={(e) => { setEntryType(e.target.value); setEntryCity(''); }} sx={TOUCH_SX}>
                    <FormControlLabel value="Air-Port" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label="Airport" />
                    <FormControlLabel value="Sea-Port" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label="Port" />
                    <FormControlLabel value="Land-Port" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label="Land border" />
                  </RadioGroup>
                  <Box ref={setFieldRef('entryCity')}>
                    <TextField fullWidth label={<>{t('pointOfEntry')}<RequiredMark /></>} value={entryCity} onChange={(e) => setEntryCity(e.target.value)} select
                      error={showErr('entryCity')} helperText={showErr('entryCity') ? t('required') : ' '}
                      sx={fieldSx}>
                      {entryCities.map((c) => <MenuItem key={c.id} value={c.title}>{c.title}</MenuItem>)}
                    </TextField>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField label={t('arrivalDate')} type="date" value={arrivalDate} onChange={(e) => setArrivalDate(e.target.value)}
                      InputLabelProps={{ shrink: true }} inputProps={{ max: new Date().toISOString().slice(0, 10) }}
                      sx={{ flex: 1, minWidth: 150, ...fieldSx }} />
                    <TextField label={t('arrivalTime')} type="time" value={arrivalTime} onChange={(e) => setArrivalTime(e.target.value)}
                      InputLabelProps={{ shrink: true }} sx={{ flex: 1, minWidth: 130, ...fieldSx }} />
                  </Box>
                  <TextField fullWidth label={t('transportNumber')} value={transportNumber} onChange={(e) => setTransportNumber(e.target.value)}
                    inputProps={{ maxLength: 20 }} sx={fieldSx} />
                  <Box ref={setFieldRef('originCountry')}>
                    <Autocomplete
                      options={countries.map((c) => c.label)}
                      value={originCountry}
                      onChange={(_, v) => setOriginCountry(v || '')}
                      renderInput={(params) => (
                        <TextField {...params} label={<>{t('originCountry')}<RequiredMark /></>}
                          error={showErr('originCountry')} helperText={showErr('originCountry') ? t('required') : ' '}
                          sx={fieldSx} />
                      )}
                      freeSolo
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>{t('countriesVisited')}</Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${CAR.border}`, borderRadius: 2, p: 1, bgcolor: '#fff' }}>
                    <FormGroup sx={TOUCH_SX}>
                      {countries.map((c) => (
                        <FormControlLabel
                          key={c.value}
                          control={
                            <Checkbox
                              checked={countriesVisited.includes(c.label)}
                              onChange={() =>
                                setCountriesVisited((prev) =>
                                  prev.includes(c.label) ? prev.filter((x) => x !== c.label) : [...prev, c.label]
                                )
                              }
                              sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }}
                            />
                          }
                          label={c.label}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                </FormCard>

                <FormCard title={t('secHealth')}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1.25, p: 1.5,
                    bgcolor: '#EEF7F5', borderRadius: 2, border: '1px solid #B2DFDB',
                    fontSize: 12, color: CAR.tealDeep, fontWeight: 500,
                  }}>
                    {t('tempNote')}
                  </Box>

                  <Box ref={setFieldRef('symptoms')}>
                    <Typography variant="body2" sx={{ mb: 1, color: showErr('symptoms') ? CAR.red : CAR.textDark }}>
                      {t('symptoms')}<RequiredMark />
                    </Typography>
                    <FormGroup sx={TOUCH_SX}>
                      {SYMPTOM_KEYS.map((k) => (
                        <FormControlLabel key={k} control={
                          <Checkbox checked={!!symptoms[k]} onChange={() => toggleSymptom(k)}
                            sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />
                        } label={t(k)} />
                      ))}
                      <FormControlLabel control={
                        <Checkbox checked={!!symptoms.none} onChange={() => toggleSymptom('none')}
                          sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />
                      } label={t('none')} />
                    </FormGroup>
                    {showErr('symptoms') && <FormHelperText error>{t('required')}</FormHelperText>}
                  </Box>
                  <TextField fullWidth label={t('otherSymptom')} value={symptomOther}
                    onChange={(e) => setSymptomOther(e.target.value.slice(0, 200))} disabled={!!symptoms.none} sx={fieldSx} />

                  <Box ref={setFieldRef('contactSick')}>
                    <Typography variant="body2" sx={{ mb: 0.5, color: showErr('contactSick') ? CAR.red : CAR.textDark }}>
                      {t('contactSick')}<RequiredMark />
                    </Typography>
                    <RadioGroup row value={contactSick} onChange={(e) => setContactSick(e.target.value as 'yes' | 'no')} sx={TOUCH_SX}>
                      <FormControlLabel value="yes" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label={t('yes')} />
                      <FormControlLabel value="no" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label={t('no')} />
                    </RadioGroup>
                    {showErr('contactSick') && <FormHelperText error>{t('required')}</FormHelperText>}
                  </Box>

                  <Box ref={setFieldRef('epidemicArea')}>
                    <Typography variant="body2" sx={{ mb: 0.5, color: showErr('epidemicArea') ? CAR.red : CAR.textDark }}>
                      {t('epidemicArea')}<RequiredMark />
                    </Typography>
                    <RadioGroup row value={epidemicArea} onChange={(e) => setEpidemicArea(e.target.value as 'yes' | 'no')} sx={TOUCH_SX}>
                      <FormControlLabel value="yes" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label={t('yes')} />
                      <FormControlLabel value="no" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label={t('no')} />
                    </RadioGroup>
                    {showErr('epidemicArea') && <FormHelperText error>{t('required')}</FormHelperText>}
                    {epidemicArea === 'yes' && (
                      <TextField fullWidth sx={{ mt: 1, ...fieldSx }} label={t('epidemicDetails')} value={epidemicDetails} onChange={(e) => setEpidemicDetails(e.target.value)} />
                    )}
                  </Box>

                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>{t('yellowFever')}</Typography>
                    <RadioGroup row value={yellowFever} onChange={(e) => setYellowFever(e.target.value as 'yes' | 'no')} sx={TOUCH_SX}>
                      <FormControlLabel value="yes" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label={t('yes')} />
                      <FormControlLabel value="no" control={<Radio sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />} label={t('no')} />
                    </RadioGroup>
                    {yellowFever === 'yes' && (
                      <TextField sx={{ mt: 1, ...fieldSx }} label={t('yellowFeverDate')} type="date" value={yellowFeverDate}
                        onChange={(e) => setYellowFeverDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                    )}
                  </Box>
                  <TextField fullWidth label={t('otherVaccinations')} value={otherVaccinations}
                    onChange={(e) => setOtherVaccinations(e.target.value.slice(0, 200))} sx={fieldSx} />
                </FormCard>

                <Box ref={setFieldRef('declaration')} sx={{
                  p: 2, mb: 2, borderRadius: 2,
                  bgcolor: showErr('declaration') ? '#fdecea' : '#fafbfc',
                  border: showErr('declaration') ? `1px solid ${CAR.red}` : `1px solid ${CAR.border}`,
                }}>
                  <FormControlLabel
                    control={<Checkbox checked={declaration} onChange={(e) => setDeclaration(e.target.checked)}
                      sx={{ color: CAR.tealDeep, '&.Mui-checked': { color: CAR.tealDeep } }} />}
                    label={t('declaration')}
                  />
                  {showErr('declaration') && <FormHelperText error>{t('declarationRequired')}</FormHelperText>}
                </Box>

                <Button
                  fullWidth size="large"
                  disabled={submitting} onClick={handleSubmit}
                  sx={{
                    height: 50, borderRadius: 2.5, fontWeight: 600, fontSize: 15,
                    bgcolor: CAR.tealDeep, color: '#fff',
                    '&:hover': { bgcolor: CAR.tealMid },
                  }}
                >
                  {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('submit')}
                </Button>
              </>
            )}
          </>
        )}

        {phase === 'complete' && result && (
          <Box sx={{ textAlign: 'center' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 56, color: CAR.green, mb: 1 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: CAR.textDark, mb: 0.5 }}>{t('qrTitle')}</Typography>
            <Typography variant="body2" sx={{ color: CAR.textMuted, mb: 3, px: 1 }}>{t('successSubtitle')}</Typography>

            <Box sx={{
              display: 'inline-block', p: 2.5, bgcolor: '#fff', borderRadius: 3,
              border: `2px solid ${CAR.border}`, boxShadow: '0 8px 24px rgba(10,92,82,0.08)',
            }}>
              <img
                src={`data:image/png;base64,${result.qr_image_base64}`}
                alt="QR code"
                style={{ width: 220, height: 220, display: 'block' }}
              />
            </Box>

            <Chip label={`${t('qrRef')}: ${result.reference_id}`}
              sx={{ mt: 2.5, fontWeight: 700, px: 1, py: 2.2, fontSize: '0.9rem', bgcolor: CAR.tealLight, color: CAR.tealDeep }} />
            <Typography sx={{ mt: 2, fontWeight: 700, fontSize: '1.1rem', color: CAR.textDark }}>{fullName}</Typography>
            <Typography variant="body2" sx={{ color: CAR.textMuted }}>{dialCode}{phoneLocal}</Typography>
            <Typography variant="body2" sx={{ mt: 2, color: CAR.textMuted, px: 2 }}>{t('qrInstruction')}</Typography>

            <Button
              fullWidth size="large"
              disabled={!passReady}
              component="a"
              href={passReady ? publicScreeningAPI.passPdfUrl(result.screening_id) : undefined}
              target="_blank" rel="noopener noreferrer"
              sx={{
                mt: 3, py: 1.5, borderRadius: 2, fontWeight: 700,
                bgcolor: CAR.tealDeep, color: '#fff', '&:hover': { bgcolor: CAR.tealMid },
              }}
            >
              {passReady ? t('downloadPass') : <><CircularProgress size={18} sx={{ mr: 1, color: '#fff' }} />{t('downloadPassWaiting')}</>}
            </Button>

            <Button
              fullWidth size="large" startIcon={<AddCircleOutlineIcon />}
              onClick={resetScreening}
              sx={{ mt: 2, py: 1.3, borderRadius: 2, fontWeight: 600, bgcolor: CAR.green, color: '#fff', '&:hover': { bgcolor: '#228B22' } }}
            >
              {t('newScreening')}
            </Button>
          </Box>
        )}

        <Snackbar
          open={showReadySnackbar}
          onClose={(_e, reason) => reason !== 'clickaway' && setShowReadySnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="success" onClose={() => setShowReadySnackbar(false)}>{t('passReadyMsg')}</Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};

export default PublicScreeningPage;
